import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Share2, Copy, Tag } from 'lucide-react';
import Header from 'pages/HomePage/components/Header';
import Footer from 'pages/HomePage/components/Footer';
import { useArticleBySlug, usePublishedArticles } from 'hooks/useArticles';
import { useRevealAnimation } from 'hooks/useRevealAnimation';
import { Article } from 'types/article';
import './ArticleDetailPage.scss';

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDate = (iso: string) =>
  new Intl.DateTimeFormat('vi-VN', {
    day: 'numeric', month: 'long', year: 'numeric',
  }).format(new Date(iso));

const handleShare = async (title: string) => {
  if (navigator.share) {
    await navigator.share({ title, url: window.location.href });
  } else {
    await navigator.clipboard.writeText(window.location.href);
    alert('Đã sao chép liên kết!');
  }
};

// ── Related Card ──────────────────────────────────────────────────────────────

const RelatedCard: React.FC<{ art: Article }> = ({ art }) => (
  <Link to={`/tin-tuc/${art.slug}`} className="article-related__card">
    <div className="article-related__img-wrap">
      {art.thumbnail
        ? <img src={art.thumbnail} alt={art.title} className="article-related__img" loading="lazy" />
        : <div className="article-related__img-fallback"><span>{art.title.charAt(0)}</span></div>
      }
    </div>
    <div className="article-related__body">
      <span className="article-related__date"><Calendar size={11} /> {formatDate(art.createdAt)}</span>
      <h4 className="article-related__title">{art.title}</h4>
    </div>
  </Link>
);

// ── Page ──────────────────────────────────────────────────────────────────────

const ArticleDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data: article, loading, error } = useArticleBySlug(slug);
  const { data: allArticlesRaw } = usePublishedArticles();
  const allArticles = allArticlesRaw ?? [];
  useRevealAnimation();

  if (loading) {
    return (
      <>
        <Header />
        <main className="article-loading">
          <div className="article-loading__spinner" />
          <p>Đang tải bài viết…</p>
        </main>
        <Footer />
      </>
    );
  }

  // Bài không tìm thấy, lỗi, hoặc chưa published → redirect 404
  if (error || !article || !article.published) {
    return <Navigate to="/trang-chu" replace />;
  }

  const related = allArticles
    .filter(a => a.id !== article.id && a.articleType === article.articleType)
    .slice(0, 3);

  const typeBadge = article.articleType === 'MKL' ? 'Tin tức GHTM' : article.articleType === 'INDUSTRY' ? 'Tin ngành' : null;

  return (
    <>
      <Header />
      <main className="article-detail">
        <div className="article-detail__container">

          {/* Back button */}
          <Link to="/tin-tuc" className="article-detail__back">
            <ArrowLeft size={16} /> Quay lại Tin tức
          </Link>

          {/* Article header */}
          <header className="article-detail__header">
            {typeBadge && (
              <span className={`article-detail__badge article-detail__badge--${article.articleType === 'MKL' ? 'ghtm' : 'nganh'}`}>
                <Tag size={11} /> {typeBadge}
              </span>
            )}
            <h1 className="article-detail__title">{article.title}</h1>
            <div className="article-detail__meta">
              <span className="article-detail__meta-item"><Calendar size={13} /> {formatDate(article.createdAt)}</span>
              <span className="article-detail__meta-item"><User size={13} /> {article.author}</span>
            </div>
          </header>

          {/* Thumbnail */}
          {article.thumbnail && (
            <div className="article-detail__thumb-wrap">
              <img src={article.thumbnail} alt={article.title} className="article-detail__thumb" />
            </div>
          )}

          {/* Excerpt */}
          {article.excerpt && (
            <p className="article-detail__excerpt">{article.excerpt}</p>
          )}

          {/* Content (raw HTML from CMS) */}
          <div
            className="article-detail__content"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Share bar */}
          <div className="article-detail__share">
            <button
              className="article-detail__share-btn"
              onClick={() => handleShare(article.title)}
              aria-label="Chia sẻ bài viết"
            >
              <Share2 size={15} /> Chia sẻ
            </button>
            <button
              className="article-detail__share-btn"
              onClick={() => navigator.clipboard.writeText(window.location.href).then(() => alert('Đã sao chép!'))}
              aria-label="Sao chép liên kết"
            >
              <Copy size={15} /> Sao chép liên kết
            </button>
          </div>

          {/* Related articles */}
          {related.length > 0 && (
            <section className="article-related">
              <h2 className="article-related__heading">Bài viết liên quan</h2>
              <div className="article-related__grid">
                {related.map(art => <RelatedCard key={art.id} art={art} />)}
              </div>
            </section>
          )}

        </div>
      </main>
      <Footer />
    </>
  );
};

export default ArticleDetailPage;
